import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { parseTranches, serializeTranches } from "./policyFixtures";
import { TrancheList } from "./TrancheList";

/**
 * TimeHeldTranche — the `tranche` policy config for the Time Held Tranche
 * policy (days-held thresholds). A Domain Component: it owns the tranche
 * value's parse/serialize and the domain unit ("days"), and composes the
 * domain-free `TrancheList` GC.
 */
export function TimeHeldTranche({
    value,
    onChange,
    rowCursorKeyPrefix,
    activeAmountRow,
    amountRowPhase,
    activeMultiplierRow,
    multiplierRowPhase,
    addCursorKey,
    addForceHover,
    addForcePressed
}: {
    value: string;
    onChange: (next: string) => void;
    /** Sim-only cursor/force seams forwarded to the underlying TrancheList. */
    rowCursorKeyPrefix?: string;
    activeAmountRow?: number | null;
    amountRowPhase?: "hover" | "press" | null;
    activeMultiplierRow?: number | null;
    multiplierRowPhase?: "hover" | "press" | null;
    addCursorKey?: string;
    addForceHover?: boolean;
    addForcePressed?: boolean;
}): React.ReactElement {
    const tranches = parseTranches(value);

    const setAmount = (i: number, amount: number | null): void =>
        onChange(
            serializeTranches(
                tranches.map((t, idx) => (idx === i ? { ...t, amount } : t))
            )
        );
    const setMultiplier = (i: number, multiplier: number | null): void =>
        onChange(
            serializeTranches(
                tranches.map((t, idx) => (idx === i ? { ...t, multiplier } : t))
            )
        );
    // Append an EMPTY row — the user types its amount + multiplier in.
    const add = (): void =>
        onChange(
            serializeTranches([...tranches, { amount: null, multiplier: null }])
        );
    const remove = (i: number): void =>
        onChange(serializeTranches(tranches.filter((_, idx) => idx !== i)));

    return (
        <TrancheList
            tranches={tranches}
            amountLabel="Days held"
            amountAdornment={
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-neutral shrink-0"
                >
                    days
                </Text>
            }
            onAmountChange={setAmount}
            onMultiplierChange={setMultiplier}
            onRemove={remove}
            onAdd={add}
            rowCursorKeyPrefix={rowCursorKeyPrefix}
            activeAmountRow={activeAmountRow}
            amountRowPhase={amountRowPhase}
            activeMultiplierRow={activeMultiplierRow}
            multiplierRowPhase={multiplierRowPhase}
            addCursorKey={addCursorKey}
            addForceHover={addForceHover}
            addForcePressed={addForcePressed}
        />
    );
}
